import { type ComponentProps, forwardRef } from 'react'
import { clsx } from 'clsx'
import * as RadioGroup from '@radix-ui/react-radio-group'
import { type RadioGroupItemProps, type RadioGroupProps } from '@radix-ui/react-radio-group'
import { Badge as UIBadge, type BadgeProps as UIBadgeProps } from 'ui/src/components/Badge/Badge'
import { Button as UIButton } from 'ui/src/components/Button/Button'
import { Card } from 'ui/src/components/Card/Card'
import { PolymorphicRef } from 'ui/src/components/TypeUtils'
import { yStack } from 'ui/src/patterns/stack'
import { badge, button, item, ribbon } from './CardRadioGroup.css'

function Root({ children, className, ...forwardedProps }: RadioGroupProps) {
  return (
    <RadioGroup.Root className={clsx(yStack({ gap: 'xs' }), className)} {...forwardedProps}>
      {children}
    </RadioGroup.Root>
  )
}

function Item({ children, className, value, ...forwardedProps }: RadioGroupItemProps) {
  return (
    <RadioGroup.Item className={clsx(item, className)} value={value} {...forwardedProps} asChild>
      <Card.Root variant="primary" asChild>
        <div>{children}</div>
      </Card.Root>
    </RadioGroup.Item>
  )
}

function Ribbon({ children, className, ...forwardedProps }: ComponentProps<typeof Card.Ribbon>) {
  return (
    <Card.Ribbon className={clsx(ribbon, className)} {...forwardedProps}>
      {children}
    </Card.Ribbon>
  )
}

function Badge({
  children,
  className,
  isSelected,
  ...forwardedProps
}: Omit<UIBadgeProps, 'size'> & { isSelected?: boolean }) {
  return (
    <UIBadge
      className={clsx(badge, className)}
      variant={isSelected ? 'green1' : 'outline'}
      size="medium"
      {...forwardedProps}
    >
      {children}
    </UIBadge>
  )
}

const Button = forwardRef(function Button(
  props: ComponentProps<typeof UIButton>,
  ref: PolymorphicRef<typeof UIButton>,
) {
  const { children, className, ...forwardedProps } = props
  return (
    <UIButton ref={ref} className={clsx(button, className)} {...forwardedProps}>
      {children}
    </UIButton>
  )
})
Button.displayName = 'CardRadioGroup.Button'

export const CardRadioGroup = {
  Root,
  Item,
  Ribbon,
  Badge,
  Button,
}
